import React from 'react'

import _ from 'lodash/fp'
import moment from 'moment'
import { connect } from 'react-redux'

import * as scorecardThings from '../../reducers/scorecard'

import { selectors as flightsSelectors } from '../../reducers/matches/flights'
import { selectors as matchesSelectors } from '../../reducers/matches/matches'

import * as Toolbox from '@material-ui/core'
import colors from '@fe/ui/colors'

const shotColors = {
  3: colors.bordeaux,
  2: colors.primaryDarkRed,
  1: colors.lightOrange,
  0: colors.pieLightBlue,
  '-1': colors.pieNormalBlue,
  '-2': colors.primaryDarkBlue,
  '-3': colors.primaryDarkGreen,
  '-4': colors.lightGreen
}

const enhance = _.flow(
  connect(
    (s, p) => {
      const gameId = _.get('gameId', p)
      const getHoleInfo = scorecardThings.makeGetHoleInfo()
      const getGameShots = scorecardThings.makeGetGameShots()
      const getGameStrokes = scorecardThings.makeGetGameStrokes()

      const mapStateToProps = (s) => {
        const flight = flightsSelectors.getFlightFromGameId(gameId)(s)
        const flightId = _.get('flightId', flight)
        const props = {
          flightId,
          gameId
        }

        const match = matchesSelectors.getMatchFromFlightId(flightId)(s)

        const game = _.get(`matches.games.items.${gameId}`, s)
        const holeInfo = getHoleInfo(s, props)
        const holesTotalShots = getGameShots(s, props)
        const holesStrokes = getGameStrokes(s, props)

        const holesPoints = _.mapValues.convert({ cap: false })(
          (strokes, holeId) => {
            const shots = _.getOr(0, holeId, holesTotalShots)
            return shots === 0
              ? 0
              : Math.max((_.get(`holesHcpOrder.${holeId}.par`, holeInfo) + strokes + 2 - shots), 0)
          },
          holesStrokes
        )

        const decoratedGame = {
          ...game,
          holesTotalShots,
          holesStrokes,
          holesPoints
        }

        const cellStyle = {
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '5px 12px'
        }

        const magic18 = _.size(_.get('holesHandicapsOrders', decoratedGame)) === 18
        const columns = _.flow(
          _.getOr({}, 'holesHandicapsOrders'),
          _.sortBy('holeOrder'),
          _.map((hho) => {
            const holeId = _.get('holeId', hho)
            const holeOrder = _.get('holeOrder', hho)
            const par = _.get('par', hho)
            const hcp = _.get('handicap', hho)
            const strokes = _.get(`holesStrokes.${holeId}`, decoratedGame)
            const shots = _.getOr(0, `holesTotalShots.${holeId}`, decoratedGame)
            const points = _.getOr(0, `holesPoints.${holeId}`, decoratedGame)

            const style = cellStyle
            const shotStyle = {
              ...cellStyle,
              backgroundColor: shots !== 0
                ? _.get(Math.min(3, Math.max(-4, (shots - par))), shotColors)
                : 'transparent',
              color: shots !== 0
                ? colors.white
                : colors.black
            }

            return {
              style,
              holeId,
              holeOrder,
              par,
              hcp,
              strokes,
              shots,
              shotStyle,
              points
            }
          }),
          _.chunk(9),
          _.map.convert({ cap: false })((columnChunk, chunkIndex) => {
            const holeId = _.sumBy('holeId', columnChunk)
            const holeOrder = <span>{magic18 ? `${chunkIndex === 0 ? 'Out' : 'In'}` : String.fromCharCode(160)}</span> // 160 is charcode for &nbsp;
            const par = _.sumBy('par', columnChunk)
            const hcp = <span>&nbsp;</span>
            const strokes = _.sumBy('strokes', columnChunk)
            const shots = _.sumBy('shots', columnChunk)
            const points = _.sumBy('points', columnChunk)

            const style = {
              ...cellStyle,
              backgroundColor: colors.paleBlue,
              color: colors.secondaryDarkGrey
            }

            const summationColumn = {
              style,
              holeId,
              holeOrder,
              par,
              hcp,
              strokes,
              shots,
              shotStyle: style,
              points
            }
            return _.concat(columnChunk, [summationColumn])
          }))(decoratedGame)

        return {
          game: decoratedGame,
          columns,
          cellStyle,
          flight,
          match,
          holeInfo
        }
      }
      return mapStateToProps
    },
    (d, p) => {
      return {}
    }
  )
)

const Scorecard = ({ game, holeInfo, flight, cellStyle, columns, match }) => {
  const canRender = () => {
    return !(_.isEmpty(game) || _.isEmpty(holeInfo) || _.isEmpty(flight) || _.isEmpty(match))
  }

  if (!canRender()) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <p>Fetching game data...</p>
      </div>
    )
  }

  const renderColumn = (column, index) => (
    <div
      key={_.get('holeId', column)}
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        backgroundColor: index % 2 === 0 ? colors.white : colors.secondaryLightGrey
      }}
    >
      <div style={_.flow(_.get('style'), _.extend({ fontWeight: 'bold' }))(column)}>{_.get('holeOrder', column)}</div>
      <div style={_.get('style', column)}>{_.get('par', column)}</div>
      <div style={_.get('style', column)}>{_.get('hcp', column)}</div>
      <div style={_.get('style', column)}>{_.get('strokes', column)}</div>
      <div style={_.get('shotStyle', column)}>{_.get('shots', column)}</div>
      <div style={_.get('style', column)}>{_.get('points', column)}</div>
    </div>
  )

  const labelStyle = {
    ...cellStyle,
    backgroundColor: colors.paleBlue,
    color: colors.secondaryDarkGrey
  }

  const labels = (
    <div
      style={{
        display: 'flex',
        flex: 1,
        flexDirection: 'column'
      }}
    >
      <div style={labelStyle}>&nbsp;</div>
      <div style={labelStyle}>Par</div>
      <div style={labelStyle}>HCP</div>
      <div style={labelStyle}>CH</div>
      <div style={labelStyle}>Shots</div>
      <div style={labelStyle}>Points</div>
    </div>
  )

  const renderColumnChunk = (chunk, index) => {
    return (
      <Toolbox.Card
        key={index}
        style={{
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '20px 0px'
        }}
      >
        {labels}
        {_.map.convert({ cap: false })(renderColumn, chunk)}
      </Toolbox.Card>
    )
  }

  const playedAt = moment(_.get('playedAt', game)).format('dddd Do MMMM YYYY')
  const totalShots = _.flow(_.get('holesTotalShots'), _.values, _.sum)(game)
  const totalPoints = _.flow(_.get('holesPoints'), _.values, _.sum)(game)
  const totalPar = _.flow(_.get('holesHandicapsOrders'), _.map(_.get('par')), _.sum)(game)
  const sumCellStyle = {
    ...cellStyle,
    color: colors.white
  }
  const sumCellStyleNumber = {
    ...sumCellStyle,
    fontSize: 20,
    fontWeight: 'bold'
  }
  return (
    <Toolbox.Card
      raised
      style={{
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '20px'
      }}
    >
      <Toolbox.CardHeader
        title={_.get('title', match)}
        subtitle={playedAt}
      />
      <div
        style={{
          padding: '0px 20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'wrap'

        }}
      >
        {_.map.convert({ cap: false })(renderColumnChunk, columns)}
      </div>
      <div
        style={{
          margin: 20,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          alignItems: 'center',
          backgroundColor: colors.primaryDarkBlue
        }}
      >
        <div>
          <div style={sumCellStyleNumber}>{totalPar}</div>
          <div style={sumCellStyle}>Par</div>
        </div>
        <div>
          <div style={sumCellStyleNumber}>{_.get('handicap', game)}</div>
          <div style={sumCellStyle}>CH</div>
        </div>
        <div>
          <div style={sumCellStyleNumber}>{totalShots}</div>
          <div style={sumCellStyle}>Shots</div>
        </div>
        <div>
          <div style={sumCellStyleNumber}>{totalPoints}</div>
          <div style={sumCellStyle}>Points</div>
        </div>
      </div>
    </Toolbox.Card>
  )
}

export default enhance(Scorecard)
